// src/pages/ProjectDetail.jsx

import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { FaExternalLinkAlt, FaArrowLeft } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { projects } from '../data/projects';
import AimTrainer from '../components/AimTrainer';
import useDocumentTitle from '../hooks/useDocumentTitle';
import './ProjectDetail.css';

const GAMES = {
  'aim-trainer': AimTrainer,
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
};

export default function ProjectDetail() {
  const { slug } = useParams();
  const project = projects.find((p) => p.slug === slug);

  useDocumentTitle(
    project ? project.title : 'Project not found',
    project ? project.description : undefined
  );

  if (!project) return <Navigate to="/projects" replace />;

  const { caseStudy } = project;
  const Game = GAMES[project.slug];

  return (
    <section className="project-detail page-container">
      <Link to="/projects" className="back-link">
        <FaArrowLeft /> All projects
      </Link>

      <motion.header
        className="project-detail-header"
        initial="hidden"
        animate="visible"
        variants={fadeUp}
      >
        <h1>{project.title}</h1>
        <p className="project-detail-tagline">{project.tagline}</p>

        <div className="project-detail-tags">
          {project.tech.map((t) => (
            <span key={t} className="tech-tag">{t}</span>
          ))}
        </div>

        {project.demoLink && (
          <div className="project-detail-actions">
            <a href={project.demoLink} target="_blank" rel="noopener noreferrer" className="btn primary">
              <FaExternalLinkAlt /> Live Demo
            </a>
          </div>
        )}
      </motion.header>

      {Game && (
        <motion.div
          className="project-detail-game card"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={fadeUp}
        >
          <h2>Play it</h2>
          <Game />
        </motion.div>
      )}

      {caseStudy.pending ? (
        <motion.div
          className="case-study-pending card"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <p>
            The full case study for this project (problem, approach, and key
            technical decisions) is being written up. In the meantime:
          </p>
          <p>{project.description}</p>
        </motion.div>
      ) : (
        <motion.div
          className="case-study"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={fadeUp}
        >
          <div className="case-study-block">
            <h2>Problem</h2>
            <p>{caseStudy.problem}</p>
          </div>

          <div className="case-study-block">
            <h2>Approach</h2>
            <p>{caseStudy.approach}</p>
          </div>

          {caseStudy.decisions?.length > 0 && (
            <div className="case-study-block">
              <h2>Key Decisions</h2>
              <ul className="decisions-list">
                {caseStudy.decisions.map((d) => (
                  <li key={d.title}>
                    <strong>{d.title}.</strong> {d.detail}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="case-study-block">
            <h2>Outcome</h2>
            <p>{caseStudy.outcome}</p>
          </div>
        </motion.div>
      )}
    </section>
  );
}
